import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FunnelIcon,
  XMarkIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ChevronDownIcon,
  Bars3Icon,
  MapPinIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '@/store';
import { fetchCategoryBySlug, fetchCategoryPath } from '@/store/slices/categoriesSlice';
import {
  filterProducts,
  setFilters,
  clearAllFilters,
  setPage,
  setSkip,
} from '@/store/slices/productFiltersSlice';
import { productsApi } from '@/services/api';
import { Product } from '@/types';
import { ROUTES } from '@/utils/constants';
import { cn } from '@/utils/cn';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { SkeletonProductGrid } from '@/components/ui/Skeleton';
import ProductCard from '@/components/ui/ProductCard';
import CategorySidebar, { CategorySidebarMobile } from '@/components/navigation/CategorySidebar';
import { fadeInUp } from '@/utils/animations';

// Sample filter data (these would come from API in production)
const BRAND_OPTIONS = ['Apple', 'Samsung', 'Sony', 'LG', 'Dell', 'HP', 'Lenovo', 'Asus'];
const CONDITION_OPTIONS = ['New', 'Like New', 'Good', 'Fair', 'For Parts'];
const PRICE_RANGES = [
  { label: 'Under $25', min: 0, max: 25 },
  { label: '$25 - $50', min: 25, max: 50 },
  { label: '$50 - $100', min: 50, max: 100 },
  { label: '$100 - $250', min: 100, max: 250 },
  { label: 'Over $250', min: 250, max: undefined },
];

const CategoryProductsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { categorySlug, parentSlug, childSlug } = useParams<{
    categorySlug?: string;
    parentSlug?: string;
    childSlug?: string;
  }>();

  // Determine which slug to use (child slug takes precedence for nested routes)
  const activeSlug = childSlug || categorySlug;

  const { currentCategory, categoryPath, isLoading: isCategoryLoading, error: categoryError } = useSelector(
    (state: RootState) => state.categories
  );
  const {
    filteredProducts,
    isLoading: isFilterLoading,
    total,
    page,
    pages,
    limit,
    skip,
    product_types,
    search_query,
    locations: selectedLocations,
  } = useSelector((state: RootState) => state.productFilters);

  const [showFilters, setShowFilters] = useState(false);
  const [showCategoryNav, setShowCategoryNav] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [localSearchQuery, setLocalSearchQuery] = useState(search_query || '');
  const [sortBy, setSortBy] = useState<string>('relevance');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Horizontal filter dropdowns
  const [showBrandsDropdown, setShowBrandsDropdown] = useState(false);
  const [showConditionDropdown, setShowConditionDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState<{ min?: number; max?: number } | null>(null);

  // Fetch category by slug
  useEffect(() => {
    if (activeSlug) {
      dispatch(fetchCategoryBySlug(activeSlug));
    }
  }, [activeSlug, dispatch]);

  // Fetch category path for breadcrumbs
  useEffect(() => {
    if (currentCategory?.id) {
      dispatch(fetchCategoryPath(currentCategory.id));
    }
  }, [currentCategory?.id, dispatch]);

  // Filter products by category slug
  useEffect(() => {
    if (activeSlug) {
      // Clear filters first
      dispatch(clearAllFilters());

      // Set category filter using slug
      setTimeout(() => {
        dispatch(
          setFilters({
            category_ids: [],
            category_slugs: [activeSlug],
            product_types: [],
            brands: [],
            conditions: [],
            locations: [],
            min_price: undefined,
            max_price: undefined,
            search_query: '',
          })
        );

        // Trigger filter
        setTimeout(() => {
          dispatch(filterProducts() as any);
        }, 100);
      }, 100);
    }
  }, [activeSlug, dispatch]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (activeSlug) {
        dispatch(
          setFilters({
            category_slugs: [activeSlug],
            product_types,
            brands: [],
            conditions: [],
            locations: selectedLocations,
            min_price: undefined,
            max_price: undefined,
            search_query: localSearchQuery,
          })
        );
        setTimeout(() => dispatch(filterProducts() as any), 100);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [localSearchQuery, product_types, selectedLocations, activeSlug, dispatch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowBrandsDropdown(false);
      setShowConditionDropdown(false);
      setShowPriceDropdown(false);
      setShowLocationDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handlePageChange = (newPage: number) => {
    const newSkip = (newPage - 1) * limit;
    dispatch(setSkip(newSkip));
    dispatch(setPage(newPage));
    setTimeout(() => dispatch(filterProducts() as any), 100);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    if (activeSlug) {
      dispatch(clearAllFilters());
      setLocalSearchQuery('');
      setSelectedBrands([]);
      setSelectedConditions([]);
      setSelectedPriceRange(null);
      // Re-apply category filter
      setTimeout(() => {
        dispatch(
          setFilters({
            category_slugs: [activeSlug],
            product_types: [],
            brands: [],
            conditions: [],
            locations: [],
            min_price: undefined,
            max_price: undefined,
            search_query: '',
          })
        );
        setTimeout(() => dispatch(filterProducts() as any), 100);
      }, 100);
    }
  };

  // Handle filter changes
  const handleBrandToggle = (brand: string) => {
    const newBrands = selectedBrands.includes(brand)
      ? selectedBrands.filter(b => b !== brand)
      : [...selectedBrands, brand];
    setSelectedBrands(newBrands);
    // Apply filter (in production, this would dispatch to Redux)
  };

  const handleConditionToggle = (condition: string) => {
    const newConditions = selectedConditions.includes(condition)
      ? selectedConditions.filter(c => c !== condition)
      : [...selectedConditions, condition];
    setSelectedConditions(newConditions);
  };

  const handlePriceRangeSelect = (range: { min?: number; max?: number } | null) => {
    setSelectedPriceRange(range);
    setShowPriceDropdown(false);
  };

  // Build breadcrumb items from category path
  const breadcrumbItems = [
    { label: 'Home', path: ROUTES.HOME },
    ...categoryPath.map((cat) => ({
      label: cat.name,
      path: `/category/${cat.slug}`,
    })),
  ];

  const currentFilters = useSelector((state: RootState) => state.productFilters);
  const hasActiveFilters =
    currentFilters.product_types.length > 0 ||
    currentFilters.brands.length > 0 ||
    currentFilters.conditions.length > 0 ||
    currentFilters.locations.length > 0 ||
    currentFilters.min_price !== undefined ||
    currentFilters.max_price !== undefined ||
    currentFilters.search_query.trim() !== '' ||
    selectedBrands.length > 0 ||
    selectedConditions.length > 0 ||
    selectedPriceRange !== null;

  const isLoadingProducts = isFilterLoading;
  const displayProducts = filteredProducts;
  const totalPages = pages > 0 ? pages : Math.ceil((total || filteredProducts.length) / limit);
  const currentPage = page > 0 ? page : 1;

  // Handle category loading error
  if (categoryError && !isCategoryLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Category Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The category you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate(ROUTES.HOME)}>Go to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
        {/* Mobile Category Navigation */}
        <CategorySidebarMobile
          isOpen={showCategoryNav}
          onClose={() => setShowCategoryNav(false)}
        />

        {/* Breadcrumbs */}
        <div className="bg-white dark:bg-dark-850 border-b border-gray-200 dark:border-dark-700">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        <div className="flex">
          {/* Category Navigation Sidebar - Desktop (flush with edge like ProductsPageSimple) */}
          <aside className="hidden lg:block w-72 flex-shrink-0 bg-white dark:bg-dark-850 border-r border-gray-200 dark:border-dark-700">
            <div className="sticky top-0 h-screen overflow-y-auto">
              <CategorySidebar collapsible={false} className="border-0 rounded-none h-full" />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
              {/* Horizontal Filter Bar */}
              <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700 p-4 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Location Filter */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowLocationDropdown(!showLocationDropdown);
                        setShowBrandsDropdown(false);
                        setShowConditionDropdown(false);
                        setShowPriceDropdown(false);
                      }}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:border-primary-500 transition-colors"
                    >
                      <MapPinIcon className="h-5 w-5" />
                      <span>Location</span>
                      <ChevronDownIcon className={cn("h-4 w-4 transition-transform", showLocationDropdown && "rotate-180")} />
                    </button>
                    <AnimatePresence>
                      {showLocationDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400 p-2">Select location...</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Brands Filter */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowBrandsDropdown(!showBrandsDropdown);
                        setShowLocationDropdown(false);
                        setShowConditionDropdown(false);
                        setShowPriceDropdown(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors",
                        selectedBrands.length > 0
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400"
                          : "border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:border-primary-500"
                      )}
                    >
                      <span>Brands {selectedBrands.length > 0 && `(${selectedBrands.length})`}</span>
                      <ChevronDownIcon className={cn("h-4 w-4 transition-transform", showBrandsDropdown && "rotate-180")} />
                    </button>
                    <AnimatePresence>
                      {showBrandsDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            {BRAND_OPTIONS.map((brand) => (
                              <label
                                key={brand}
                                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedBrands.includes(brand)}
                                  onChange={() => handleBrandToggle(brand)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{brand}</span>
                              </label>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Condition Filter */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConditionDropdown(!showConditionDropdown);
                        setShowLocationDropdown(false);
                        setShowBrandsDropdown(false);
                        setShowPriceDropdown(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors",
                        selectedConditions.length > 0
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400"
                          : "border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:border-primary-500"
                      )}
                    >
                      <span>Condition {selectedConditions.length > 0 && `(${selectedConditions.length})`}</span>
                      <ChevronDownIcon className={cn("h-4 w-4 transition-transform", showConditionDropdown && "rotate-180")} />
                    </button>
                    <AnimatePresence>
                      {showConditionDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            {CONDITION_OPTIONS.map((condition) => (
                              <label
                                key={condition}
                                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedConditions.includes(condition)}
                                  onChange={() => handleConditionToggle(condition)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{condition}</span>
                              </label>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Price Range Filter */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPriceDropdown(!showPriceDropdown);
                        setShowLocationDropdown(false);
                        setShowBrandsDropdown(false);
                        setShowConditionDropdown(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors",
                        selectedPriceRange
                          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400"
                          : "border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 hover:border-primary-500"
                      )}
                    >
                      <span>{selectedPriceRange ? PRICE_RANGES.find(r => r.min === selectedPriceRange.min)?.label || 'Price Range' : 'Price Range'}</span>
                      <ChevronDownIcon className={cn("h-4 w-4 transition-transform", showPriceDropdown && "rotate-180")} />
                    </button>
                    <AnimatePresence>
                      {showPriceDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg z-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-2">
                            <button
                              onClick={() => handlePriceRangeSelect(null)}
                              className={cn(
                                "w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-dark-700",
                                !selectedPriceRange ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                              )}
                            >
                              All Prices
                            </button>
                            {PRICE_RANGES.map((range) => (
                              <button
                                key={range.label}
                                onClick={() => handlePriceRangeSelect({ min: range.min, max: range.max })}
                                className={cn(
                                  "w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-dark-700",
                                  selectedPriceRange?.min === range.min ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300"
                                )}
                              >
                                {range.label}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Results Count */}
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {total} {total === 1 ? 'result' : 'results'}
                  </span>

                  {/* View Toggle */}
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-dark-700 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={cn(
                        'p-2 rounded',
                        viewMode === 'grid'
                          ? 'bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400'
                          : 'text-gray-600 dark:text-gray-400'
                      )}
                    >
                      <Squares2X2Icon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={cn(
                        'p-2 rounded',
                        viewMode === 'list'
                          ? 'bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400'
                          : 'text-gray-600 dark:text-gray-400'
                        )}
                      >
                        <ListBulletIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Mobile Buttons */}
                    <div className="flex items-center gap-2 lg:hidden">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCategoryNav(true)}
                      >
                        <Bars3Icon className="h-5 w-5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        <FunnelIcon className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>

                {/* Active Filters - inside filter bar container */}
                {hasActiveFilters && (
                  <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Active filters:</span>
                    {product_types.map((type) => (
                      <Badge key={type} variant="primary" className="cursor-pointer">
                        {type === 'brand_new' ? 'Brand New' : type === 'recycled' ? 'Recycled' : type}
                        <XMarkIcon
                          className="h-4 w-4 ml-1"
                          onClick={() => {
                            const newTypes = product_types.filter((t) => t !== type);
                            dispatch(setFilters({ product_types: newTypes }));
                            setTimeout(() => dispatch(filterProducts() as any), 100);
                          }}
                        />
                      </Badge>
                    ))}
                    {search_query && (
                      <Badge variant="primary" className="cursor-pointer">
                        Search: {search_query}
                        <XMarkIcon
                          className="h-4 w-4 ml-1"
                          onClick={() => {
                            setLocalSearchQuery('');
                            dispatch(setFilters({ search_query: '' }));
                            setTimeout(() => dispatch(filterProducts() as any), 100);
                          }}
                        />
                      </Badge>
                    )}
                    <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                      Clear all
                    </Button>
                  </div>
                )}

                {/* Results Count */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-dark-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {total}
                    </span>{' '}
                    results
                  </p>
                </div>
              </div>

              {/* Mobile Filters Sidebar */}
              {showFilters && (
                <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowFilters(false)}>
                  <div
                    className="absolute right-0 top-0 h-full w-80 bg-white dark:bg-dark-850 shadow-xl overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
                        <button
                          onClick={() => setShowFilters(false)}
                          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      {/* Brands Section */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Brands</h4>
                        <div className="space-y-2">
                          {BRAND_OPTIONS.map((brand) => (
                            <label key={brand} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedBrands.includes(brand)}
                                onChange={() => handleBrandToggle(brand)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{brand}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Condition Section */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Condition</h4>
                        <div className="space-y-2">
                          {CONDITION_OPTIONS.map((condition) => (
                            <label key={condition} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedConditions.includes(condition)}
                                onChange={() => handleConditionToggle(condition)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{condition}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Price Range Section */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Price Range</h4>
                        <div className="space-y-2">
                          <button
                            onClick={() => handlePriceRangeSelect(null)}
                            className={cn(
                              "w-full text-left px-3 py-2 text-sm rounded",
                              !selectedPriceRange ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
                            )}
                          >
                            All Prices
                          </button>
                          {PRICE_RANGES.map((range) => (
                            <button
                              key={range.label}
                              onClick={() => handlePriceRangeSelect({ min: range.min, max: range.max })}
                              className={cn(
                                "w-full text-left px-3 py-2 text-sm rounded",
                                selectedPriceRange?.min === range.min ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
                              )}
                            >
                              {range.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Clear Filters Button */}
                      {hasActiveFilters && (
                        <Button variant="outline" className="w-full" onClick={handleClearFilters}>
                          Clear All Filters
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Products Grid */}
              {isLoadingProducts || isCategoryLoading ? (
                <SkeletonProductGrid count={12} />
              ) : displayProducts.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-700">
                  <MagnifyingGlassIcon className="h-24 w-24 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                    No products found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    {hasActiveFilters
                      ? 'Try adjusting your filters or search query'
                      : 'Check back later for new products in this category'}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    {hasActiveFilters && (
                      <Button variant="outline" onClick={handleClearFilters}>Clear Filters</Button>
                    )}
                    <Button onClick={() => navigate(ROUTES.PRODUCTS)} className="bg-sky-500 hover:bg-sky-600 text-white">Browse All Products</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className={cn(
                      viewMode === 'grid'
                        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'
                        : 'space-y-4'
                    )}
                  >
                    {displayProducts.map((product: Product) => (
                      <motion.div
                        key={product.id}
                        variants={fadeInUp}
                        initial="initial"
                        animate="animate"
                      >
                        <ProductCard
                          product={product}
                          viewMode={viewMode}
                          showAddToCart={true}
                        />
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      </div>
  );
};

export default CategoryProductsPage;
